import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/context/ToastContext";
import { tasksApi } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { PrioritySelect } from "@/components/PrioritySelect";
import { Icon } from "@/components/Icon";
import { Badge } from "@/components/Badge";

export interface ChangeTaskPriorityModalProps {
    isOpen: boolean;
    task: any | null;
    onClose: () => void;
    onSuccess: (updated: any) => void;
}

export const ChangeTaskPriorityModal: React.FC<
    ChangeTaskPriorityModalProps
> = ({ isOpen, task, onClose, onSuccess }) => {
    const { t } = useTranslation("global");
    const { showSuccess } = useToast();
    const [selectedPriority, setSelectedPriority] = useState<number>(2);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (task) {
            const num =
                typeof task.priority === "number"
                    ? task.priority
                    : parseInt(task.priority, 10) || 2;
            setSelectedPriority(num);
            setError(null);
        }
    }, [task]);

    const handleSubmit = async (): Promise<void> => {
        if (!task) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await tasksApi.update(task.id, {
                priority: selectedPriority,
            });

            if (res.result) {
                showSuccess(t("tasks.modal_priority.success"));
                const updatedRow = {
                    ...task,
                    priority: selectedPriority,
                };
                onSuccess(updatedRow);
                onClose();
            } else {
                setError(res.message || t("errors.default"));
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : t("errors.default"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("tasks.modal_priority.title")}
            maxWidth="sm"
            onSubmit={handleSubmit}
            submitLabel={t("tasks.modal_priority.save_button")}
            submittingLabel={t("tasks.modal_priority.saving")}
            isSubmitting={loading}
            submitIcon={<Icon value="check" size={15} />}
        >
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center space-x-2 mb-3">
                    <Icon value="alertCircle" size={16} className="shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {task && (
                <div className="text-center mb-6">
                    <Badge value={task.code} />
                    <div className="mt-2 font-semibold">{task.title}</div>
                </div>
            )}

            <PrioritySelect
                value={selectedPriority}
                onChange={setSelectedPriority}
            />
        </Modal>
    );
};
